import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { Translation } from '@hfs/i18n/components/Translation'
import { useTranslation } from '@hfs/i18n/hooks/useTranslation'
import { Icon } from '@hfs/ui/components/Icon'
import { colors } from '@hfs/ui/tokens/colors'
import { fontFamily } from '@hfs/ui/tokens/typography'
import { phone } from '@hfs/website/constants/contacts'
import { getWhatsAppUrl } from '@hfs/website/utils/whatsapp'

const whatsappGreen = '#25D366'
const whatsappGreenDark = '#1DA851'

// Sits above the page content but below the cookie banner (999), dialogs (1001) and toasts
const Wrapper = styled.div`
  align-items: flex-end;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  font-family: ${fontFamily};
  position: fixed;
  right: 24px;
  z-index: 998;

  @media (max-width: 767px) {
    right: 16px;
  }
`

const Bubble = styled.div`
  background-color: ${colors.white};
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  margin-bottom: 12px;
  overflow: hidden;
  width: min(320px, calc(100vw - 32px));
`

const BubbleHeader = styled.div`
  align-items: center;
  background-color: ${whatsappGreen};
  color: ${colors.white};
  display: flex;
  font-size: 16px;
  font-weight: 700;
  justify-content: space-between;
  padding: 12px 16px;
`

const HeaderTitle = styled.span`
  align-items: center;
  display: flex;
  gap: 8px;
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${colors.white};
  cursor: pointer;
  line-height: 0;
  padding: 4px;
`

const BubbleBody = styled.div`
  color: ${colors.n900};
  font-size: 14px;
  line-height: 20px;
  padding: 16px;
`

const CallToAction = styled.a`
  align-items: center;
  background-color: ${whatsappGreen};
  border-radius: 24px;
  color: ${colors.white};
  display: flex;
  font-weight: 700;
  gap: 8px;
  justify-content: center;
  margin-top: 16px;
  padding: 12px 16px;
  text-decoration: none;

  &:hover {
    background-color: ${whatsappGreenDark};
  }
`

const PhoneNumber = styled.p`
  color: ${colors.n300};
  font-size: 12px;
  margin: 12px 0 0;
  text-align: center;
`

const LauncherButton = styled.button`
  align-items: center;
  background-color: ${whatsappGreen};
  border: none;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  color: ${colors.white};
  cursor: pointer;
  display: flex;
  height: 56px;
  justify-content: center;
  transition: transform 0.2s ease, background-color 0.2s ease;
  width: 56px;

  &:hover {
    background-color: ${whatsappGreenDark};
    transform: scale(1.05);
  }
`

// Floating WhatsApp entry point: a launcher button that opens a small bubble with the call to action.
// It is a plain link to wa.me, so no third-party script runs and no cookie is set.
export const WhatsAppChat = () => {
  const [open, setOpen] = useState(false)
  const { formattedMessage: label } = useTranslation({ translationId: 'global.whatsapp.label' })
  const { formattedMessage: closeLabel } = useTranslation({ translationId: 'global.whatsapp.close' })

  const toggle = useCallback(() => setOpen(current => !current), [])
  const close = useCallback(() => setOpen(false), [])

  return (
    <Wrapper>
      {open && (
        <Bubble aria-label={label} role="dialog">
          <BubbleHeader>
            <HeaderTitle>
              <Icon icon={faWhatsapp} size="lg" />
              <Translation translationId="global.whatsapp.title" />
            </HeaderTitle>

            <CloseButton aria-label={closeLabel} onClick={close} type="button">
              <Icon icon={faTimes} />
            </CloseButton>
          </BubbleHeader>

          <BubbleBody>
            <Translation translationId="global.whatsapp.greeting" />

            <CallToAction href={getWhatsAppUrl()} onClick={close} rel="noopener noreferrer" target="_blank">
              <Icon icon={faWhatsapp} size="lg" />
              <Translation translationId="global.whatsapp.cta" />
            </CallToAction>

            <PhoneNumber>{phone}</PhoneNumber>
          </BubbleBody>
        </Bubble>
      )}

      <LauncherButton aria-expanded={open} aria-label={label} onClick={toggle} type="button">
        <Icon icon={open ? faTimes : faWhatsapp} size="2x" />
      </LauncherButton>
    </Wrapper>
  )
}
